import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

interface CustomContainerProps {
  children: React.ReactNode;
  border?: boolean; // adds the grey border if true
  borderColor?: string; // adds the left colored bar if provided
  legendTitle?: string; // legend title text
  legendIcon?: React.ReactNode; // legend icon component
}

const CustomContainer: React.FC<CustomContainerProps> = ({
  children,
  border = false,
  borderColor,
  legendTitle,
  legendIcon,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        width: '97%',
        backgroundColor: 'white',
        borderRadius: 2,
        marginTop: 4,
        marginLeft: 'auto',
        marginRight: 'auto',
        display: 'flex',
        border: border ? 1 : 'none',
        borderColor: border ? '#dad0d0' : 'transparent',
        position: legendTitle ? 'relative' : 'static',
        [theme.breakpoints.down('xl')]: {
          width: '99%',
        },
      }}
    >
      {/* Left color bar if color is given */}
      {borderColor && (
        <Box
          sx={{
            width: '7px',
            backgroundColor: borderColor,
            borderTopLeftRadius: 'inherit',
            borderBottomLeftRadius: 'inherit',
            flexShrink: 0,
          }}
        />
      )}

      {/* Main content */}
      <Box
        sx={{
          flex: 1, // ensure it takes only available space
          minWidth: 0, // <-- important for flexbox shrink
          paddingTop: legendTitle ? 3 : 4,
          paddingBottom: 2,
          paddingLeft: 3,
          paddingRight: 3,
          boxSizing: 'border-box', // include padding in width calc
        }}
      >
        {children}
      </Box>

      {/* Legend */}
      {legendTitle && (
        <Box
          sx={{
            position: 'absolute',
            top: -10,
            left: borderColor ? 16 + 7 : 16,
            backgroundColor: 'white',
            px: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            zIndex: 10,
            maxWidth: '90%', // prevents it from overflowing
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {legendIcon}
          <Typography variant="body1" color="text.secondary" noWrap>
            {legendTitle}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default CustomContainer;
